import { createAsyncThunk } from '@reduxjs/toolkit';
import { db } from '../../indexedDB';
import { registerOfflineAction } from '../../helpers/actionRegistry';
import { fetchCompanyUsersRequest } from '../slices/database/companyUsersSlice';
import {
	getDatabaseRecord,
	getAllDatabaseRecord,
	patchDatabase,
	postDatabaseRecord,
	getCompanyDrivers,
	getTrucksData,
	getWorkflowTemplates,
	getAuthzPublicMenu,
	getAuthzCompanies,
	getCompanyRoles,
	updateUserRoles,
	getUserRoles,
	addUser,
	importData,
	unassignUser,
	editUser,
	addTransporterGenerator,
	editTransporterGenerator,
	deleteTransporterGenerator,
	getCRDetails,
	addTransporterGeneratorContact,
	editTransporterGeneratorContact,
	deleteTransporterGeneratorContact,
	addTransporterTruck,
	editTransporterTruck,
	deleteTransporterTruck,
	addTemplate,
	deleteTemplate,
	downloadTemplate,
	getCompanyUsers,
	getTransporterGeneratorsData,
	getCompanyBranches,
	getTransporterGeneratorContactsData,
	getDomainData,
	getServicesData,
	getUserManualsData,
	getImportDataTypeConfigs,
	importCustomData,
	deleteDatabaseRecord
} from "api/databaseApi";
import { logger } from "helpers/logger";

// #region DATABASE
export const fetchDatabaseRecord = createAsyncThunk("mds/fetchDatabaseRecord", async ({ tableName, fieldName, itemId, columns }, { rejectWithValue, signal }) => {
	try {
		const response = await getDatabaseRecord({ tableName, fieldName, itemId, columns }, signal);
		return response;
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to fetch database record:", error);
		return rejectWithValue(error.message || "Failed to fetch database record");
	}
});

export const fetchDatabaseRecords = createAsyncThunk("mds/fetchDatabaseRecords", async ({ tableName, condition, columns }, { rejectWithValue, signal }) => {
	try {
		const response = await getAllDatabaseRecord({ tableName, condition, columns }, signal);
		return response;
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to fetch database records:", error);
		return rejectWithValue(error.message || "Failed to fetch database records");
	}
});

export const patchDatabaseRecord = createAsyncThunk("mds/patchDatabaseRecord", async (params, { rejectWithValue, signal }) => {
	try {
		const { JsonToPatch } = params;

		// Pass both if HeaderParameters exists
		const response = await patchDatabase(JsonToPatch, signal);
		return response;
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to patch database record:", error);
		return rejectWithValue(error.message || "Failed to patch database record");
	}
});

export const postDatabaseRecordThunk = createAsyncThunk("mds/postDatabaseRecordThunk", async (params, { rejectWithValue, signal }) => {
	try {
		const { jsonBody } = params;

		const response = await postDatabaseRecord(jsonBody, signal);
		return response;
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to post database record:", error);
		return rejectWithValue(error.message || "Failed to post database record");
	}
});

export const deleteDatabaseRecordThunk = createAsyncThunk("mds/deleteDatabaseRecordThunk", async (params, { rejectWithValue, signal }) => {
	try {
		const jsonBody = params?.jsonBody || params?.JsonToDelete || params;

		const response = await deleteDatabaseRecord(jsonBody, signal);
		return response;
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to delete database record:", error);
		return rejectWithValue(error.message || "Failed to delete database record");
	}
});

// #endregion

// #region COMPANIES

export const fetchCompanyDriversThunk = createAsyncThunk("mds/fetchCompanyDriversThunk", async (params, { rejectWithValue, signal }) => {
	try {
		const { companyId } = params;

		const response = await getCompanyDrivers(companyId, signal);
		return response;
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to fetch company drivers:", error);
		return rejectWithValue(error.message || "Failed to fetch company drivers");
	}
});

export const fetchCompanyUsersThunk = createAsyncThunk("mds/fetchCompanyUsersThunk", async (params, { rejectWithValue }) => {
	try {
		const { companyId } = params;

		const response = await getCompanyUsers(companyId);
		return response;
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to fetch company users:", error);
		return rejectWithValue(error.message || "Failed to fetch company users");
	}
});

// #endregion

// #region WORKFLOW TEMPLATES

export const fetchWorkflowTemplatesThunk = createAsyncThunk("mds/fetchWorkflowTemplatesThunk", async (params, { rejectWithValue, signal }) => {
	try {
		const { applicationID } = params;
		const response = await getWorkflowTemplates(applicationID, signal);
		return response;
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to fetch workflow templates:", error);
		return rejectWithValue(error.message || "Failed to fetch workflow templates");
	}
});

// #endregion

// #region ROLES

export const fetchCompanyRolesThunk = createAsyncThunk(
	'mds/fetchCompanyRolesThunk',
	async (params, { rejectWithValue, signal }) => {
		try {
			const { companyId } = params;

			const response = await getCompanyRoles(companyId, signal);
			return response;
		} catch (error) {
			if (error.name === 'AbortError') {
				logger.info("🚫 Request cancelled");
				return rejectWithValue("cancelled");
			}
			logger.error("❌ Failed to fetch company roles:", error);
			return rejectWithValue(error.message || "Failed to fetch company roles");
		}
	}
);

export const patchUserRolesThunk = createAsyncThunk(
	'mds/patchUserRolesThunk',
	async (params, { rejectWithValue, signal, dispatch }) => {
		const { body } = params;

		if (!navigator.onLine) {
			await db.action_queue.add({
				actionName: 'patchUserRolesThunk',
				payload: { body },
				timestamp: Date.now(),
				status: 'pending',
			});
			return { queued: true };
		}

		try {
			const response = await updateUserRoles(body, signal);
			if (body.companyId) {
				dispatch(fetchCompanyUsersRequest({ companyId: body.companyId }));
			}
			return response;
		} catch (error) {
			if (error.name === 'AbortError') {
				logger.info("🚫 Request updateUserRoles cancelled");
				return rejectWithValue("cancelled");
			}
			logger.error("❌ Failed to update user roles:", error);
			return rejectWithValue(error.message || "Failed to update user roles");
		}
	}
);

export const getUserRolesThunk = createAsyncThunk(
	'mds/getUserRolesThunk',
	async (params, { rejectWithValue, signal }) => {
		try {
			const { userId, companyId } = params;

			const response = await getUserRoles(userId, companyId, signal);
			return response;
		} catch (error) {
			if (error.name === 'AbortError') {
				logger.info("🚫 Request getUserRoles cancelled");
				return rejectWithValue("cancelled");
			}
			logger.error("❌ Failed to fetch user roles:", error);
			return rejectWithValue(error.message || "Failed to fetch user roles");
		}
	}
);

// #endregion

// #region PUBLIC

export const fetchAuthzPublicMenuThunk = createAsyncThunk("mds/fetchAuthzPublicMenuThunk", async ({ domainId }, { rejectWithValue, signal }) => {
	try {
		const response = await getAuthzPublicMenu(domainId, signal);
		return response;
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to get authz public menu:", error);
		return rejectWithValue(error.message || "Failed to get authz public menu");
	}
});

// authz.* schema: fetches the caller's companies on demand (not tied to fetchUserCompaniesRequest's
// saga) - used by Layout's handleSwitchCompany, which needs to await the actual response inline to
// decide whether to navigate or warn, rather than fire-and-forget a saga-driven request.
export const fetchUserCompaniesThunk = createAsyncThunk("mds/fetchUserCompaniesThunk", async (params, { rejectWithValue, signal }) => {
	try {
		const response = await getAuthzCompanies(signal);
		return response;
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to get user companies:", error);
		return rejectWithValue(error.message || "Failed to get user companies");
	}
});

export const fetchDomainDataThunk = createAsyncThunk("mds/fetchDomainDataThunk", async (params, { rejectWithValue, signal }) => {
	try {
		const { domainName } = params;
		const response = await getDomainData(domainName, signal);
		return response;
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to get domain data:", error);
		return rejectWithValue(error.message || "Failed to get domain data");
	}
});

export const fetchServicesDataThunk = createAsyncThunk("mds/fetchServicesDataThunk", async (params, { rejectWithValue, signal }) => {
	try {
		const { domainId, pageName } = params;
		const response = await getServicesData(domainId, pageName, signal);
		return response;
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to get services data:", error);
		return rejectWithValue(error.message || "Failed to get services data");
	}
});

export const fetchUserManualsDataThunk = createAsyncThunk("mds/fetchUserManualsDataThunk", async (params, { rejectWithValue, signal }) => {
	try {
		const response = await getUserManualsData(signal);
		return response;
	} catch (error) {
		if (error.name === "AbortError") {
			console.log("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		return rejectWithValue(error.message || "Failed to get user manuals data");
	}
});

export const fetchImportDataTypesThunk = createAsyncThunk("mds/fetchImportDataTypesThunk", async ({ domainId }, { rejectWithValue, signal }) => {
	try {
		const response = await getImportDataTypeConfigs(domainId, signal);
		return response;
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to fetch import data types:", error);
		return rejectWithValue(error.message || "Failed to fetch import data types");
	}
});
// #endregion

// #region USERS

export const addUserThunk = createAsyncThunk("mds/addUserThunk", async (params, { rejectWithValue, dispatch }) => {
	const { companyId, keycloakCompanyId, body } = params;

	if (!navigator.onLine) {
		await db.action_queue.add({
			actionName: 'addUserThunk',
			payload: { companyId, keycloakCompanyId, body },
			timestamp: Date.now(),
			status: 'pending',
		});
		return { queued: true };
	}

	try {
		const response = await addUser(companyId, keycloakCompanyId, body);
		if (companyId) dispatch(fetchCompanyUsersRequest({ companyId }));
		return response;
	} catch (error) {
		return rejectWithValue(error.message || "Failed to add user");
	}
});

export const importDataThunk = createAsyncThunk("mds/importDataThunk", async (params, { rejectWithValue }) => {
	try {
		const { table_key, body, companyId, keycloakCompanyId } = params;
		const response = await importData(table_key, body, companyId, keycloakCompanyId);
		return response;
	} catch (error) {
		return rejectWithValue(error.message || "Failed to import data");
	}
});

export const unassignUserThunk = createAsyncThunk("mds/unassignUserThunk", async (params, { rejectWithValue, dispatch }) => {
	const { userId, companyId, keycloakCompanyId } = params;

	if (!navigator.onLine) {
		await db.action_queue.add({
			actionName: 'unassignUserThunk',
			payload: { userId, companyId, keycloakCompanyId },
			timestamp: Date.now(),
			status: 'pending',
		});
		return { queued: true };
	}

	try {
		const response = await unassignUser(userId, companyId, keycloakCompanyId);
		if (companyId) dispatch(fetchCompanyUsersRequest({ companyId }));
		return response;
	} catch (error) {
		return rejectWithValue(error.message || "Failed to unassign user");
	}
});

export const editUserThunk = createAsyncThunk("mds/editUserThunk", async (params, { rejectWithValue, signal, dispatch }) => {
	const { userId, body, companyId } = params;

	if (!navigator.onLine) {
		await db.action_queue.add({
			actionName: 'editUserThunk',
			payload: { userId, body, companyId },
			timestamp: Date.now(),
			status: 'pending',
		});
		return { queued: true };
	}

	try {
		const response = await editUser(userId, body, signal);
		if (companyId) dispatch(fetchCompanyUsersRequest({ companyId }));
		return response;
	} catch (error) {
		return rejectWithValue(error.message || "Failed to edit user");
	}
});

// ─── Offline Action Registry ──────────────────────────────────────────────────
// Register every thunk that has been upgraded with the offline-aware pattern
// (navigator.onLine check + db.action_queue + follow-up dispatch inside thunk).
//
// Pattern to follow when upgrading a new thunk:
//   1. Add `if (!navigator.onLine) { await db.action_queue.add({...}); return { queued: true }; }`
//   2. Move any follow-up dispatch (e.g. fetchCompanyUsersRequest) INTO the thunk on success
//   3. Add `registerOfflineAction('thunkName', thunkName)` below
//
// ✅ Upgraded thunks:
registerOfflineAction('editUserThunk', editUserThunk);
registerOfflineAction('addUserThunk', addUserThunk);
registerOfflineAction('unassignUserThunk', unassignUserThunk);
registerOfflineAction('patchUserRolesThunk', patchUserRolesThunk);

// #endregion


// #region GENERATORS

export const fetchTransporterGeneratorThunk = createAsyncThunk("mds/fetchTransporterGeneratorThunk", async (params, { rejectWithValue, signal }) => {
	try {
		const { companyId } = params;

		const response = await getTransporterGeneratorsData(companyId, signal);
		return response;
	} catch (error) {
		return rejectWithValue(error.message || "Failed to Get Transporter Generators.");
	}
});

export const addTransporterGeneratorThunk = createAsyncThunk("mds/addTransporterGeneratorThunk", async (params, { rejectWithValue }) => {
	try {
		const { companyId, data } = params;

		const response = await addTransporterGenerator(companyId, data);
		return response;
	} catch (error) {
		return rejectWithValue(error.message || "Failed to Add Transporter Generator.");
	}
});

export const editTransporterGeneratorThunk = createAsyncThunk("mds/editTransporterGeneratorThunk", async (params, { rejectWithValue }) => {
	try {
		const { generatorId, data } = params;

		const response = await editTransporterGenerator(generatorId, data);
		return response;
	} catch (error) {
		return rejectWithValue(error.message || "Failed to Edit Transporter Generator.");
	}
});

export const deleteTransporterGeneratorThunk = createAsyncThunk("mds/deleteTransporterGeneratorThunk", async (params, { rejectWithValue }) => {
	try {
		const { generatorId } = params;

		const response = await deleteTransporterGenerator(generatorId);
		return response;
	} catch (error) {
		return rejectWithValue(error.message || "Failed to Delete Transporter Generator.");
	}
});

// #endregion

// #region CONTACTS

export const getCompanyContactsThunk = createAsyncThunk("mds/getCompanyContactsThunk", async (params, { dispatch, rejectWithValue, signal }) => {
	try {
		const { companyId } = params;
		const condition = `companyId=eq.${companyId}`;

		const result = await dispatch(fetchDatabaseRecords({ tableName: "tbl_contacts", condition }));

		if (fetchDatabaseRecords.fulfilled.match(result)) {
			return result.payload;
		} else {
			return rejectWithValue(result.payload || "Failed to fetch company contacts.");
		}
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to fetch company contacts:", error);
		return rejectWithValue(error.message || "Failed to fetch company contacts.");
	}
});

// #endregion

// #region CONTACTS/DELEGATES
export const fetchTransporterGeneratorContactsThunk = createAsyncThunk("mds/fetchTransporterGeneratorContactsThunk", async (params, { rejectWithValue, signal }) => {
	try {
		const { companyId } = params;

		const response = await getTransporterGeneratorContactsData(companyId, signal);
		return response;
	} catch (error) {
		return rejectWithValue(error.message || "Failed to Get Transporter Generator Contact.");
	}
});

export const addTransporterGeneratorContactThunk = createAsyncThunk("mds/addTransporterGeneratorContactThunk", async (params, { rejectWithValue }) => {
	try {
		const { companyId, data } = params;

		const response = await addTransporterGeneratorContact(companyId, data);
		return response;
	} catch (error) {
		return rejectWithValue(error.message || "Failed to Add Transporter Generator Contact.");
	}
});

export const editTransporterGeneratorContactThunk = createAsyncThunk("mds/editTransporterGeneratorContactThunk", async (params, { rejectWithValue }) => {
	try {
		const { contactId, data } = params;

		const response = await editTransporterGeneratorContact(contactId, data);
		return response;
	} catch (error) {
		return rejectWithValue(error.message || "Failed to Edit Transporter Generator Contact.");
	}
});

export const deleteTransporterGeneratorContactThunk = createAsyncThunk("mds/deleteTransporterGeneratorContactThunk", async (params, { rejectWithValue }) => {
	try {
		const { contactId } = params;

		const response = await deleteTransporterGeneratorContact(contactId);
		return response;
	} catch (error) {
		return rejectWithValue(error.message || "Failed to Delete Transporter Generator Contact.");
	}
});

// #endregion

// #region TRUCKS

export const fetchTrucksThunk = createAsyncThunk("mds/fetchTrucksThunk", async (params, { rejectWithValue, signal }) => {
	try {
		const { companyId } = params;

		const response = await getTrucksData(companyId, signal);
		return response;
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to fetch company trucks:", error);
		return rejectWithValue(error.message || "Failed to fetch company trucks");
	}
});

export const addTransporterTruckThunk = createAsyncThunk("mds/addTransporterTruckThunk", async (params, { rejectWithValue }) => {
	try {
		const { relatedCompanyId, data } = params;

		const response = await addTransporterTruck(relatedCompanyId, data);
		return response;
	} catch (error) {
		return rejectWithValue(error.message || "Failed to Add Transporter Truck.");
	}
});

export const editTransporterTruckThunk = createAsyncThunk("mds/editTransporterTruckThunk", async (params, { rejectWithValue }) => {
	try {
		const { truckId, data } = params;

		const response = await editTransporterTruck(truckId, data);
		return response;
	} catch (error) {
		return rejectWithValue(error.message || "Failed to Edit Transporter Truck.");
	}
});

export const deleteTransporterTruckThunk = createAsyncThunk("mds/deleteTransporterTruckThunk", async (params, { rejectWithValue }) => {
	try {
		const { truckId } = params;

		const response = await deleteTransporterTruck(truckId);
		return response;
	} catch (error) {
		return rejectWithValue(error.message || "Failed to Delete Transporter Truck.");
	}
});

// #endregion

// #region MOC
export const getCRDetailsThunk = createAsyncThunk("mds/getCRDetailsThunk", async (params, { rejectWithValue, signal }) => {
	try {
		const { unn } = params;

		const response = await getCRDetails(unn, signal);
		return response;
	} catch (error) {
		return rejectWithValue(error.message || "Failed to Get UNN Details.");
	}
});
// #endregion

// #region TEMPLATES

export const addTemplateThunk = createAsyncThunk("mds/addTemplateThunk", async (params, { rejectWithValue }) => {
	try {
		const { templateName, templateFileExtension, templateFile } = params;

		const response = await addTemplate(templateName, templateFileExtension, templateFile);
		return response;
	} catch (error) {
		return rejectWithValue(error.message || "Failed to Add Template.");
	}
});

export const deleteTemplateThunk = createAsyncThunk("mds/deleteTemplateThunk", async (params, { rejectWithValue }) => {
	try {
		const { processedTemplateId } = params;

		const response = await deleteTemplate(processedTemplateId);
		return response;
	} catch (error) {
		return rejectWithValue(error.message || "Failed to Delete Template.");
	}
});

export const downloadTemplateThunk = createAsyncThunk("mds/downloadTemplateThunk", async (params, { rejectWithValue }) => {
	try {
		const { templateId } = params;

		const response = await downloadTemplate(templateId);
		return response;
	} catch (error) {
		return rejectWithValue(error.message || "Failed to Download Template.");
	}
});
// #endregion

// #region BRANCHES

export const fetchCompanyBranchesThunk = createAsyncThunk("mds/fetchCompanyBranchesThunk", async (params, { rejectWithValue, signal }) => {
	try {
		const { companyId } = params;

		const response = await getCompanyBranches(companyId, signal);
		return response;
	} catch (error) {
		return rejectWithValue(error.message || "Failed to Get Company Branches.");
	}
});

// #endregion



// #region IMPORT DATA
export const importCustomDataThunk = createAsyncThunk("mds/importCustomDataThunk", async (params, { rejectWithValue }) => {
	try {
		const { tableKey, bodyData } = params;
		const response = await importCustomData(tableKey, bodyData);
		return response;
	} catch (error) {
		return rejectWithValue(error.message || "Failed to Import Custom Data.");
	}
});
// #endregion